import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import arService from "../../../../../services/arService";
import LineItemsGrid from "../../../../common/LineItemsGrid";
import "./CreditNoteForm.css";
import { showMessageBox } from '../../../../site/fn';

const CreditNoteForm = ({ rowId }) => {
  const navigate = useNavigate();
  const isEditMode = rowId !== null && rowId !== undefined;

  const [formData, setFormData] = useState({
    credit_note_date: new Date().toISOString().split("T")[0],
    customer_id: "",
    project_id: "",
    city: "",
    state: "",
    location: "",
    original_invoice_id: "",
    original_invoice_no: "",
    original_invoice_date: "",
    work_order_no: "",
    work_order_date: "",
    buyer_order_no: "",
    buyer_order_date: "",
    period_from: "",
    period_to: "",
    reason: "",
    credit_type: "Crane Parts Return",
    taxable_amount: 0,
    cgst_percentage: 0,
    cgst_amount: 0,
    sgst_percentage: 0,
    sgst_amount: 0,
    igst_percentage: 0,
    igst_amount: 0,
    total_amount: 0,
    is_draft: true,
    terms_of_payment: "",
    dispatch_doc_no: "",
    dispatch_through: "",
    destination: "",
    terms_of_delivery: "",
    remarks: "",
    approver_id: "",
  });

  const [lineItems, setLineItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchApprovers();
    if (isEditMode) {
      fetchCreditNote();
    }
  }, [rowId]);

  useEffect(() => {
    if (formData.customer_id) {
      fetchCustomerProjects(formData.customer_id);
    } else {
      setProjects([]);
      setInvoices([]);
      setLineItems([]);
      setFormData((prev) => ({ 
        ...prev, 
        project_id: "", 
        city: "",
        state: "",
        location: "",
        original_invoice_id: "",
        original_invoice_no: "",
        work_order_no: "",
        work_order_date: "",
        period_from: "",
        period_to: ""
      }));
    }
  }, [formData.customer_id]);

  useEffect(() => {
    if (formData.project_id) {
      fetchProjectInvoices(formData.project_id);
    } else {
      setInvoices([]);
      setLineItems([]);
      setFormData((prev) => ({ 
        ...prev, 
        original_invoice_id: "",
        original_invoice_no: "",
        work_order_no: "",
        work_order_date: "",
        period_from: "",
        period_to: ""
      }));
    }
  }, [formData.project_id]);

  useEffect(() => {
    calculateTotals();
  }, [lineItems, formData.cgst_percentage, formData.sgst_percentage, formData.igst_percentage]);

  const fetchCustomers = async () => {
    try {
      const data = await arService.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await arService.getCreditNoteApprovers();
      setApprovers(response || []);
    } catch (error) {
      console.error("Error fetching approvers:", error);
      setApprovers([]);
    }
  };

  const fetchCustomerProjects = async (customerId) => {
    try {
      setIsLoadingProjects(true);
      const response = await arService.getProjectsByCustomer(customerId);
      console.log('Customer projects loaded:', response);
      setProjects(response || []);
      if (!response || response.length === 0) {
        console.warn('No projects found for customer:', customerId);
      }
    } catch (error) {
      console.error("Error fetching customer projects:", error);
      setProjects([]);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchProjectInvoices = async (projectId) => {
    try {
      setIsLoadingInvoices(true);
      const response = await arService.getInvoicesByProject(projectId);
      console.log('Project invoices loaded:', response);
      setInvoices(response || []);
      if (!response || response.length === 0) {
        console.warn('No invoices found for project:', projectId);
      }
    } catch (error) {
      console.error("Error fetching project invoices:", error);
      setInvoices([]);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const fetchCreditNote = async () => {
    try {
      setIsLoading(true);
      const data = await arService.getCreditNoteById(rowId);
      
      setFormData({
        credit_note_date: data.credit_note_date ? data.credit_note_date.split("T")[0] : "",
        customer_id: data.customer_id || "",
        project_id: data.project_id || "",
        city: data.city || "",
        state: data.state || "",
        location: data.location || "",
        original_invoice_id: data.original_invoice_id || "",
        original_invoice_no: data.original_invoice_no || "",
        original_invoice_date: data.original_invoice_date ? data.original_invoice_date.split("T")[0] : "",
        work_order_no: data.work_order_no || "",
        work_order_date: data.work_order_date ? data.work_order_date.split("T")[0] : "",
        buyer_order_no: data.buyer_order_no || "",
        buyer_order_date: data.buyer_order_date ? data.buyer_order_date.split("T")[0] : "",
        period_from: data.period_from ? data.period_from.split("T")[0] : "",
        period_to: data.period_to ? data.period_to.split("T")[0] : "",
        reason: data.reason || "",
        credit_type: data.credit_type || "Crane Parts Return",
        taxable_amount: data.taxable_amount || 0,
        cgst_percentage: data.cgst_percentage || 0,
        sgst_percentage: data.sgst_percentage || 0,
        igst_percentage: data.igst_percentage || 0,
        cgst_amount: data.cgst_amount || 0,
        sgst_amount: data.sgst_amount || 0,
        igst_amount: data.igst_amount || 0,
        total_amount: data.total_amount || 0,
        currency: data.currency || "INR",
        is_draft: data.is_draft !== undefined ? data.is_draft : true,
        terms_of_payment: data.terms_of_payment || "",
        dispatch_doc_no: data.dispatch_doc_no || "",
        dispatch_through: data.dispatch_through || "",
        destination: data.destination || "",
        terms_of_delivery: data.terms_of_delivery || "",
        remarks: data.remarks || "",
        approver_id: data.approver_id || "",
      });

      setLineItems(data.line_items || []);
    } catch (error) {
      console.error("Error fetching credit note:", error);
      showMessageBox("Failed to fetch credit note details.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotals = () => {
    const taxableAmount = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    
    // Calculate tax amounts from percentages
    const cgstPercentage = parseFloat(formData.cgst_percentage) || 0;
    const sgstPercentage = parseFloat(formData.sgst_percentage) || 0;
    const igstPercentage = parseFloat(formData.igst_percentage) || 0;
    
    const cgst = (taxableAmount * cgstPercentage) / 100;
    const sgst = (taxableAmount * sgstPercentage) / 100;
    const igst = (taxableAmount * igstPercentage) / 100;
    
    const total = taxableAmount + cgst + sgst + igst;

    setFormData((prev) => ({
      ...prev,
      taxable_amount: taxableAmount.toFixed(2),
      cgst_amount: cgst.toFixed(2),
      sgst_amount: sgst.toFixed(2),
      igst_amount: igst.toFixed(2),
      total_amount: total.toFixed(2),
    }));
  };

  const handleProjectChange = async (e) => {
    const projectId = e.target.value;
    
    if (!projectId) {
      setFormData((prev) => ({
        ...prev,
        project_id: "",
        city: "",
        state: "",
        location: "",
      }));
      return;
    }

    // Find selected project from the list (compare as strings since API returns strings)
    const selectedProject = projects.find(p => p.projectId === projectId);
    
    if (selectedProject) {
      setFormData((prev) => ({
        ...prev,
        project_id: projectId,
        city: selectedProject.city || "",
        state: selectedProject.stateName || "",
        location: selectedProject.location || "",
      }));
    }
  };

  const mapInvoiceLineItems = (items) =>
    (items || []).map((item, index) => ({
      line_no: item.line_no ?? index + 1,
      description: item.description || "",
      hsn_code: item.hsn_code || "",
      quantity: parseFloat(item.quantity) || 0,
      unit: item.unit || "",
      rate: parseFloat(item.rate) || 0,
      amount: (
        (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)
      ).toFixed(2),
    }));

  const handleInvoiceChange = async (e) => {
    const invoiceId = e.target.value;
    
    if (!invoiceId) {
      setLineItems([]);
      setFormData((prev) => ({
        ...prev,
        original_invoice_id: "",
        original_invoice_no: "",
        original_invoice_date: "",
        work_order_no: "",
        work_order_date: "",
        period_from: "",
        period_to: "",
      }));
      return;
    }

    try {
      const response = await arService.getWorkOrderDetailsByInvoice(invoiceId);
      console.log('Work order details:', response);
      
      setFormData((prev) => ({
        ...prev,
        original_invoice_id: invoiceId,
        original_invoice_no: response.invoiceNumber || "",
        original_invoice_date: response.invoiceDate ? new Date(response.invoiceDate).toISOString().split("T")[0] : "",
        work_order_no: response.workOrder?.workOrderNumber || "",
        work_order_date: response.workOrder?.workOrderDate ? new Date(response.workOrder.workOrderDate).toISOString().split("T")[0] : "",
        period_from: response.workOrder?.startDate ? new Date(response.workOrder.startDate).toISOString().split("T")[0] : "",
        period_to: response.workOrder?.endDate ? new Date(response.workOrder.endDate).toISOString().split("T")[0] : "",
        cgst_percentage: response.taxDetails?.cgstPercentage || 0,
        sgst_percentage: response.taxDetails?.sgstPercentage || 0,
        igst_percentage: response.taxDetails?.igstPercentage || 0,
      }));

      if (!isEditMode) {
        const invoiceLineItems = mapInvoiceLineItems(response.line_items);
        setLineItems(invoiceLineItems);
        if (invoiceLineItems.length === 0) {
          showMessageBox("No line items found for the selected invoice.", "info");
        }
      }
    } catch (error) {
      console.error("Error fetching work order details:", error);
      const selectedInvoice = invoices.find(inv => inv.invoiceId === parseInt(invoiceId));
      if (selectedInvoice) {
        setFormData((prev) => ({
          ...prev,
          original_invoice_id: invoiceId,
          original_invoice_no: selectedInvoice.invoiceNumber || "",
          original_invoice_date: selectedInvoice.invoiceDate ? new Date(selectedInvoice.invoiceDate).toISOString().split("T")[0] : "",
        }));
      }
      if (!isEditMode) {
        setLineItems([]);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLineItemsChange = (updatedItems) => {
    setLineItems(updatedItems);
  };

  const validateForm = () => {
    if (!formData.customer_id) {
      showMessageBox("Please select a customer.", "error");
      return false;
    }
    if (!formData.credit_note_date) {
      showMessageBox("Please enter credit note date.", "error");
      return false;
    }
    if (!formData.reason) {
      showMessageBox("Please enter reason for credit note.", "error");
      return false;
    }
    if (!formData.original_invoice_id) {
      showMessageBox("Please select an original invoice.", "error");
      return false;
    }
    if (lineItems.length === 0) {
      showMessageBox("Please add at least one line item.", "error");
      return false;
    }
    const invalidQuantity = lineItems.some(
      (item) => Number.isNaN(parseFloat(item.quantity))
    );
    if (invalidQuantity) {
      showMessageBox("Please enter a valid quantity for each line item.", "error");
      return false;
    }
    const invalidRate = lineItems.some(
      (item) => parseFloat(item.rate) < 0 || Number.isNaN(parseFloat(item.rate))
    );
    if (invalidRate) {
      showMessageBox("Rate cannot be negative. Please enter a valid rate for each line item.", "error");
      return false;
    }
    const totalAmount = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    if (totalAmount <= 0) {
      showMessageBox("Total credit note amount must be greater than zero.", "error");
      return false;
    }
    if (!formData.is_draft && !formData.approver_id) {
      showMessageBox("Please select an approver or keep Save as Draft checked.", "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSaving(true);

      const payload = {
        ...formData,
        line_items: lineItems,
      };

      if (isEditMode) {
        await arService.updateCreditNote(rowId, payload);
        showMessageBox("Credit Note updated successfully!", "success");
      } else {
        await arService.createCreditNote(payload);
        showMessageBox("Credit Note created successfully!", "success");
      }

      navigate("/ar/credit-notes");
    } catch (error) {
      console.error("Error saving credit note:", error);
      showMessageBox("Failed to save credit note. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel? Any unsaved changes will be lost.")) {
      navigate("/ar/credit-notes");
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading credit note...</p>
      </div>
    );
  }

  return (
    <div className="credit-note-form-container">
      <div className="form-header">
        <h2>{isEditMode ? "Edit Credit Note" : "Create New Credit Note"}</h2>
      </div>

      <form onSubmit={handleSubmit} className="credit-note-form">
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="credit_note_date">
                Credit Note Date <span className="required">*</span>
              </label>
              <input
                type="date"
                id="credit_note_date"
                name="credit_note_date"
                value={formData.credit_note_date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="customer_id">
                Customer <span className="required">*</span>
              </label>
              <select
                id="customer_id"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="project_id">
                Project <span className="required">*</span>
              </label>
              <select
                id="project_id"
                name="project_id"
                value={formData.project_id}
                onChange={handleProjectChange}
                disabled={!formData.customer_id || isLoadingProjects}
                required
              >
                <option value="">
                  {isLoadingProjects ? 'Loading projects...' : 
                   !formData.customer_id ? 'Select Customer First' :
                   projects.length === 0 ? 'No projects available' : 'Select Project'}
                </option>
                {projects.map((project) => (
                  <option key={project.projectId} value={project.projectId}>
                    {project.projectName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                readOnly
                placeholder="Auto-populated from project"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                readOnly
                placeholder="Auto-populated from project"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

          </div>
        </div>

        {/* Invoice & Order Details */}
        <div className="form-section">
          <h3>Invoice & Order Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="original_invoice_id">
                Original Invoice No <span className="required">*</span>
              </label>
              <select
                id="original_invoice_id"
                name="original_invoice_id"
                value={formData.original_invoice_id}
                onChange={handleInvoiceChange}
                disabled={!formData.project_id || isLoadingInvoices || isEditMode}
                required
              >
                <option value="">
                  {isLoadingInvoices ? 'Loading invoices...' : 
                   !formData.project_id ? 'Select Project First' :
                   invoices.length === 0 ? 'No invoices available' : 'Select Invoice'}
                </option>
                {invoices.map((invoice) => (
                  <option key={invoice.invoiceId} value={invoice.invoiceId}>
                    {invoice.invoiceNumber} - ₹{(invoice.amount || 0).toLocaleString()} ({new Date(invoice.invoiceDate).toLocaleDateString()})
                  </option>
                ))}
              </select>
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Line items are auto-loaded from the selected invoice
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="original_invoice_date">Original Invoice Date</label>
              <input
                type="date"
                id="original_invoice_date"
                name="original_invoice_date"
                value={formData.original_invoice_date}
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="work_order_no">Work Order No</label>
              <input
                type="text"
                id="work_order_no"
                name="work_order_no"
                value={formData.work_order_no}
                readOnly
                placeholder="Auto-populated from invoice"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="work_order_date">Work Order Date</label>
              <input
                type="date"
                id="work_order_date"
                name="work_order_date"
                value={formData.work_order_date}
                readOnly
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="period_from">Period From</label>
              <input
                type="date"
                id="period_from"
                name="period_from"
                value={formData.period_from}
                readOnly
                placeholder="Auto-populated from work order"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="period_to">Period To</label>
              <input
                type="date"
                id="period_to"
                name="period_to"
                value={formData.period_to}
                readOnly
                placeholder="Auto-populated from work order"
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="form-section">
          <h3>Reason for Credit Note</h3>
          <div className="form-group full-width">
            <label htmlFor="credit_type">
              Credit Type <span className="required">*</span>
            </label>
            <select
              id="credit_type"
              name="credit_type"
              value={formData.credit_type}
              onChange={handleInputChange}
              required
            >
              <option value="Crane Parts Return">Crane Parts Return</option>
              <option value="Equipment Return">Equipment Return</option>
              <option value="Service Cancellation">Service Cancellation</option>
              <option value="Transport Charges Adjustment">Transport Charges Adjustment</option>
              <option value="Mobility Service Refund">Mobility Service Refund</option>
              <option value="Operations Downtime Credit">Operations Downtime Credit</option>
              <option value="Defective Parts">Defective Parts</option>
              <option value="Wrong Parts Supplied">Wrong Parts Supplied</option>
              <option value="Maintenance Service Adjustment">Maintenance Service Adjustment</option>
              <option value="Rental Credit">Rental Credit</option>
              <option value="Price Correction">Price Correction</option>
              <option value="Discount Applied">Discount Applied</option>
              <option value="Damaged During Transport">Damaged During Transport</option>
              <option value="Quality Issue">Quality Issue</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="form-group full-width">
            <label htmlFor="reason">
              Reason <span className="required">*</span>
            </label>
            <textarea
              id="reason"
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows="3"
              placeholder="Enter reason for issuing credit note..."
              required
            ></textarea>
          </div>
        </div>

        {/* Line Items */}
        <div className="form-section">
          <LineItemsGrid
            lineItems={lineItems}
            onChange={handleLineItemsChange}
            variant="credit-note"
          />
        </div>

        {/* Amount Details */}
        <div className="form-section">
          <h3>Amount Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="taxable_amount">Taxable Amount</label>
              <input
                type="number"
                id="taxable_amount"
                name="taxable_amount"
                value={formData.taxable_amount}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgst_percentage">CGST %</label>
              <input
                type="number"
                id="cgst_percentage"
                name="cgst_percentage"
                value={formData.cgst_percentage}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cgst_amount">CGST Amount</label>
              <input
                type="number"
                id="cgst_amount"
                name="cgst_amount"
                value={formData.cgst_amount}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgst_percentage">SGST %</label>
              <input
                type="number"
                id="sgst_percentage"
                name="sgst_percentage"
                value={formData.sgst_percentage}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sgst_amount">SGST Amount</label>
              <input
                type="number"
                id="sgst_amount"
                name="sgst_amount"
                value={formData.sgst_amount}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="igst_percentage">IGST %</label>
              <input
                type="number"
                id="igst_percentage"
                name="igst_percentage"
                value={formData.igst_percentage}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="igst_amount">IGST Amount</label>
              <input
                type="number"
                id="igst_amount"
                name="igst_amount"
                value={formData.igst_amount}
                readOnly
                className="readonly-field"
              />
            </div>

            <div className="form-group">
              <label htmlFor="total_amount">Total Amount</label>
              <input
                type="number"
                id="total_amount"
                name="total_amount"
                value={formData.total_amount}
                readOnly
                className="readonly-field total-amount"
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="form-section">
          <h3>Additional Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="terms_of_payment">Terms of Payment</label>
              <input
                type="text"
                id="terms_of_payment"
                name="terms_of_payment"
                value={formData.terms_of_payment}
                onChange={handleInputChange}
                placeholder="Payment within 30 days"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dispatch_doc_no">Dispatch Document No</label>
              <input
                type="text"
                id="dispatch_doc_no"
                name="dispatch_doc_no"
                value={formData.dispatch_doc_no}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="dispatch_through">Dispatch Through</label>
              <input
                type="text"
                id="dispatch_through"
                name="dispatch_through"
                value={formData.dispatch_through}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="destination">Destination</label>
              <input
                type="text"
                id="destination"
                name="destination"
                value={formData.destination}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="terms_of_delivery">Terms of Delivery</label>
              <input
                type="text"
                id="terms_of_delivery"
                name="terms_of_delivery"
                value={formData.terms_of_delivery}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="remarks">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                rows="3"
                placeholder="Additional remarks..."
              ></textarea>
            </div>
          </div>
        </div>

        {/* Approval Section */}
        <div className="form-section">
          <h3>Approval</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="approver_id">
                Select Approver <span className="required">*</span>
              </label>
              <select
                id="approver_id"
                name="approver_id"
                value={formData.approver_id}
                onChange={handleInputChange}
                required={!formData.is_draft}
              >
                <option value="">-- Select Approver --</option>
                {approvers.map((approver) => (
                  <option key={approver.id} value={approver.id}>
                    {approver.full_name} ({approver.email})
                  </option>
                ))}
              </select>
              <small className="help-text">
                Select a user with CREDIT_NOTE_APPROVER role
              </small>
            </div>
          </div>
        </div>

        {/* Draft Checkbox */}
        <div className="form-section">
          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_draft"
                checked={formData.is_draft}
                onChange={handleInputChange}
              />
              <span>Save as Draft</span>
            </label>
            <small className="help-text">
              Draft credit notes can be edited later. Uncheck to submit for approval.
            </small>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSaving}>
            {isSaving ? "Saving..." : isEditMode ? "Update Credit Note" : "Create Credit Note"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreditNoteForm;
